import React from 'react';
import MuiDrawer, { DrawerProps } from '@mui/material/Drawer';
import type { Theme, CSSObject } from '@mui/material/styles';
import SparkDrawerContextProvider from 'components/Providers/ContextProviders/SparkDrawerContext';
import { Box } from '@mui/material';

const openedMixin = (
  theme: Theme,
  drawerWidth: number,
  fullWidth: boolean
): CSSObject => ({
  width: fullWidth ? '100%' : drawerWidth,
  bgcolor: 'white',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  borderLeft: `1px solid ${theme.palette.borderColor}`,
  cursor: 'default',
  scrollbarWidth: 'none',
});

const closedMixin = (
  theme: Theme,
  closeWidth: CSSObject['width']
): CSSObject => ({
  bgcolor: 'white',
  width: closeWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});

const DEFAULT_DRAWER_WIDTH = 500;

type SparkDrawerProps = {
  fullWidth?: boolean;
  hideOnClose?: boolean;
  resizable?: boolean;
  open: boolean;
  children: React.ReactNode;
  closeWidth?: CSSObject['width'];
  openWidth?: number;
  variant?: DrawerProps['variant'];
  anchor?: DrawerProps['anchor'];
  top?: string;
  drawerClasses?: string;
  drawerHeight?: string;
};

function SparkDrawer({
  open,
  fullWidth = false,
  openWidth = DEFAULT_DRAWER_WIDTH,
  hideOnClose = false,
  resizable = false,
  children,
  closeWidth = 0,
  variant = 'permanent',
  anchor = 'left',
  top = '4rem',
  drawerClasses = '',
  drawerHeight = `calc(100vh - ${top})`,
}: SparkDrawerProps) {
  const [drawerWidth, setDrawerWidth] = React.useState(DEFAULT_DRAWER_WIDTH);
  const [isResizing, setIsResizing] = React.useState(false);
  const [drawerRect, setDrawerRect] = React.useState<DOMRect | undefined>(
    undefined
  );

  React.useEffect(() => {
    setDrawerWidth(openWidth);
  }, [openWidth]);

  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setDrawerRect(entry.contentRect);
        }
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
    };
  }, [setDrawerRect]);

  const drawerCloseWidth = React.useMemo(() => {
    if (hideOnClose) {
      return 0;
    }
    return closeWidth;
  }, [closeWidth, hideOnClose]);

  const handleResizeMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (!resizable) {
        return;
      }
      const startX = e.clientX;
      const startWidth = drawerWidth;

      const handleMouseMove = (event: MouseEvent) => {
        event.stopPropagation();
        setIsResizing(true);
        const width = startWidth - event.clientX + startX;
        //TODO need to pass min,max from props
        if (width >= 300 && width <= 1800) {
          setDrawerWidth(width);
        }
      };

      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        setIsResizing(false);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [drawerWidth, resizable]
  );
  const renderResizeBorders = () => {
    if (!resizable) return null;

    return (
      <Box
        key={anchor}
        onMouseDown={(e) => handleResizeMouseDown(e)}
        sx={{
          position: 'absolute',
          ...(anchor === 'left' && {
            top: 0,
            bottom: 0,
            right: 0,
            width: '2px',
            cursor: 'ew-resize',
          }),
          ...(anchor === 'right' && {
            top: 0,
            bottom: 0,
            left: 0,
            width: '2px',
            cursor: 'ew-resize',
          }),
          backgroundColor: (theme) =>
            isResizing ? theme.palette.primary.main : 'transparent',
          '&:hover': {
            backgroundColor: (theme) => theme.palette.primary.main,
          },
          zIndex: 10,
        }}
      />
    );
  };

  return (
    <MuiDrawer
      variant={variant}
      open={open}
      component='div'
      anchor={anchor}
      className={drawerClasses}
      sx={(theme) => ({
        position: 'relative',
        '& .MuiPaper-root': {
          top: top,
          borderLeft: `1px solid ${theme.palette.borderColor}`,
          height: drawerHeight,
        },
        ...(open && {
          ...openedMixin(theme, drawerWidth, fullWidth),
          '& .MuiDrawer-paper': openedMixin(theme, drawerWidth, fullWidth),
        }),
        ...(!open && {
          ...closedMixin(theme, drawerCloseWidth),
          '& .MuiDrawer-paper': closedMixin(theme, drawerCloseWidth),
        }),
      })}
      PaperProps={{
        component: 'div',
        ref: ref,
      }}
    >
      {renderResizeBorders()}
      <SparkDrawerContextProvider initialRectModal={drawerRect}>
        {children}
      </SparkDrawerContextProvider>
    </MuiDrawer>
  );
}

export default SparkDrawer;
